// NewRouteModal.jsx - Create new routes with airport search
// Smart airport search across ICAO, IATA, city, name

import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useWorldState } from "../state/WorldStateContext.jsx";
import { GLOBAL_AIRPORTS } from "../../data/GlobalAirports.js";
import { AIRPORTS as MAIN_AIRPORTS } from "../../data/airports.js";
import { AIRCRAFT_DATABASE } from "../../data/aircraft.js";
import { Button } from "@/components/ui/button";
// Icons removed - text-only UI

// Get runway length for an airport (from MAIN_AIRPORTS if available)
const getRunwayLength = (code) => {
  return MAIN_AIRPORTS[code]?.runwayLength || null;
};

// Check if aircraft can land at airport based on runway length
const canAircraftLand = (aircraftModel, airportCode) => {
  const aircraft = AIRCRAFT_DATABASE[aircraftModel];
  const runwayLength = getRunwayLength(airportCode);

  if (!aircraft?.minRunway || !runwayLength) {
    return { canLand: true, reason: null }; // Unknown - allow it
  }

  if (runwayLength < aircraft.minRunway) {
    return {
      canLand: false,
      reason: `Runway too short: ${runwayLength.toLocaleString()} ft (needs ${aircraft.minRunway.toLocaleString()} ft)`
    };
  }

  return { canLand: true, reason: null };
};

// Format currency
const formatMoney = (amount) => {
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
  return `$${amount}`;
};

// Calculate distance between two airports in nm
function calcDistance(lat1, lon1, lat2, lon2) {
  const R = 3440.065; // Earth radius in nm
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return Math.round(R * c);
}

// Airport Search Component
function AirportSearch({ value, onChange, placeholder, excludeCode, allowedAirports = null, sortByDistanceFrom = null }) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get origin airport for distance calculations
  const originAirport = sortByDistanceFrom ? GLOBAL_AIRPORTS[sortByDistanceFrom] : null;

  // Filter airports by search
  const filteredAirports = useMemo(() => {
    // If allowedAirports is provided, only search those
    const airportSource = allowedAirports
      ? allowedAirports.reduce((acc, code) => {
          if (GLOBAL_AIRPORTS[code]) acc[code] = GLOBAL_AIRPORTS[code];
          return acc;
        }, {})
      : GLOBAL_AIRPORTS;

    // Helper to add distance to airport object
    const addDistance = (code, airport) => {
      if (originAirport && airport.lat && airport.lon) {
        const dist = calcDistance(originAirport.lat, originAirport.lon, airport.lat, airport.lon);
        return { code, ...airport, distance: dist };
      }
      return { code, ...airport, distance: 99999 };
    };

    if (!search.trim()) {
      // Show available airports when no search - sorted by distance from origin
      if (allowedAirports) {
        // Simulation mode - show all allowed airports sorted by distance
        const airports = Object.entries(airportSource)
          .filter(([code]) => code !== excludeCode)
          .map(([code, airport]) => addDistance(code, airport));

        // Sort by distance (closest first)
        if (originAirport) {
          airports.sort((a, b) => a.distance - b.distance);
        }
        return airports;
      }
      // Simple mode - show major/large airports sorted by distance (type to search for smaller ones)
      const airports = Object.entries(GLOBAL_AIRPORTS)
        .filter(([code]) => code !== excludeCode)
        .filter(([_, a]) => a.hub_tier === 'major' || a.hub_tier === 'large')
        .map(([code, airport]) => addDistance(code, airport));

      // Sort by distance (closest first)
      if (originAirport) {
        airports.sort((a, b) => a.distance - b.distance);
      }
      return airports; // Major/large airports only - search for smaller ones
    }

    const s = search.toLowerCase();
    const results = [];

    // Search through airports
    for (const [code, airport] of Object.entries(airportSource)) {
      if (code === excludeCode) continue;

      // Priority scoring
      let score = 0;

      // Exact ICAO match = highest priority
      if (code.toLowerCase() === s) score = 100;
      // Exact IATA match
      else if (airport.iata?.toLowerCase() === s) score = 90;
      // ICAO starts with search
      else if (code.toLowerCase().startsWith(s)) score = 80;
      // IATA starts with search
      else if (airport.iata?.toLowerCase().startsWith(s)) score = 70;
      // City starts with search
      else if (airport.city?.toLowerCase().startsWith(s)) score = 60;
      // Name contains search
      else if (airport.name?.toLowerCase().includes(s)) score = 40;
      // City contains search
      else if (airport.city?.toLowerCase().includes(s)) score = 30;
      // Country matches
      else if (airport.country?.toLowerCase().includes(s)) score = 20;
      else continue;

      // Boost major hubs
      if (airport.hub_tier === 'major') score += 5;
      else if (airport.hub_tier === 'large') score += 3;

      const withDist = addDistance(code, airport);
      results.push({ ...withDist, score });
    }

    // Sort by score (no limit - show all matches)
    return results.sort((a, b) => b.score - a.score);
  }, [search, excludeCode, allowedAirports, originAirport]);

  const selectedAirport = value ? GLOBAL_AIRPORTS[value] : null;

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? search : (value ? `${value} - ${selectedAirport?.city || selectedAirport?.name || ''}` : '')}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            setIsOpen(true);
            if (value) setSearch("");
          }}
          placeholder={placeholder}
          className="w-full px-4 py-3 rounded-lg bg-zinc-900 text-white"
          style={{ border: "1px solid #525252" }}
        />
        {value && !isOpen && (
          <button
            onClick={() => { onChange(null); setSearch(""); }}
            className="absolute right-3 top-1/2 -translate-y-1/2"
            style={{ color: "#737373", fontWeight: "bold" }}
          >
            x
          </button>
        )}
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 overflow-y-auto rounded-lg bg-zinc-900"
          style={{ border: "1px solid #525252", maxHeight: "400px" }}
        >
          {filteredAirports.length === 0 ? (
            <div className="p-3 text-sm" style={{ color: "#737373" }}>
              No airports found for "{search}"
            </div>
          ) : (
            filteredAirports.map((airport) => (
              <button
                key={airport.code}
                onClick={() => {
                  onChange(airport.code);
                  setSearch("");
                  setIsOpen(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-zinc-800 flex items-center justify-between"
                style={{ borderBottom: "1px solid #333" }}
              >
                <div>
                  <span className="font-mono text-emerald-400">{airport.code}</span>
                  {airport.iata && (
                    <span className="text-gray-500 text-xs ml-1">({airport.iata})</span>
                  )}
                  <span className="mx-2 text-white">-</span>
                  <span className="text-white">{airport.city || airport.name}</span>
                  {airport.country && airport.country !== "US" && (
                    <span className="text-gray-500 ml-1">({airport.country})</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {airport.distance && airport.distance < 99999 && (
                    <span className="text-xs font-mono" style={{ color: '#60ffd0' }}>{airport.distance} nm</span>
                  )}
                  {airport.hub_tier === 'major' && (
                    <span className="text-xs px-1 rounded" style={{ backgroundColor: '#f59e0b', color: '#000' }}>MAJOR</span>
                  )}
                  {airport.hub_tier === 'large' && (
                    <span className="text-xs px-1 rounded" style={{ backgroundColor: '#3b82f6', color: '#fff' }}>LARGE</span>
                  )}
                </div>
              </button>
            ))
          )}
          <div className="p-2 text-xs text-center" style={{ color: "#525252" }}>
            Type ICAO, IATA, city, or airport name
          </div>
        </div>
      )}
    </div>
  );
}

// Main Modal Component
export default function NewRouteModal({ isOpen, onClose, onCreateRoute, initialDestination = null }) {
  const { worldState, addRoute } = useWorldState();
  const hub = worldState?.airline?.hub || "KPDX";
  const playerHubs = worldState?.ownedHubs || [hub]; // All hubs player owns
  const aircraft = worldState?.aircraft || [];
  const routes = worldState?.routes || [];
  const flights = worldState?.flights || [];
  const slots = worldState?.slots || {};
  const gameMode = worldState?.gameMode || 'simple';
  const isSimulationMode = gameMode === 'simulation';

  // Filter available aircraft - exclude those already assigned to routes or in-flight
  const availableAircraft = useMemo(() => {
    const assignedIds = new Set(routes.map(r => r.aircraftId).filter(Boolean));
    const inFlightIds = new Set(
      flights
        .filter(f => f.status === 'in_flight' || f.status === 'boarding' || f.status === 'taxiing')
        .map(f => f.aircraftId)
    );
    const unavailableStatuses = ['delivery', 'maintenance', 'grounded', 'sold', 'in_flight'];

    return aircraft.filter(a => {
      const isAssigned = assignedIds.has(a.id);
      const isInFlight = inFlightIds.has(a.id);
      const isUnavailable = unavailableStatuses.includes(a.status);
      return !isAssigned && !isInFlight && !isUnavailable;
    });
  }, [aircraft, routes, flights]);

  const [origin, setOrigin] = useState(hub);
  const [destination, setDestination] = useState(initialDestination);
  const [selectedAircraftId, setSelectedAircraftId] = useState(null);

  // HUB-ONLY DEPARTURES: Origin must be a hub OR a location with stranded aircraft
  // Stranded aircraft (at non-hub airports) can only fly BACK to a hub
  const playerHubSet = useMemo(() => new Set(playerHubs), [playerHubs]);

  // Find aircraft stranded at non-hub airports
  const strandedLocations = useMemo(() => {
    const locations = new Set();
    availableAircraft.forEach(ac => {
      const loc = ac.location;
      if (loc && !playerHubSet.has(loc)) {
        locations.add(loc);
      }
    });
    return Array.from(locations);
  }, [availableAircraft, playerHubSet]);

  // Allowed origins: player hubs + stranded aircraft locations
  const allowedOrigins = useMemo(() => {
    return [...playerHubs, ...strandedLocations];
  }, [playerHubs, strandedLocations]);

  // Check if current origin is a stranded location (not a hub)
  const isStrandedOrigin = origin && !playerHubSet.has(origin) && strandedLocations.includes(origin);

  // For stranded origins, destination must be a hub (return flight only)
  // For hub origins, destination can be any airport
  const allowedDestinations = useMemo(() => {
    if (isStrandedOrigin) {
      // Stranded aircraft can ONLY fly back to a hub
      return playerHubs;
    }
    if (!isSimulationMode) return null; // null = all airports (simple mode)
    return Object.keys(slots).filter(code => slots[code]?.length > 0);
  }, [isSimulationMode, slots, isStrandedOrigin, playerHubs]);

  // Reset on open (but preserve initialDestination if provided)
  useEffect(() => {
    if (isOpen) {
      setOrigin(hub);
      // Only reset destination if no initialDestination was provided
      if (!initialDestination) {
        setDestination(null);
      } else {
        setDestination(initialDestination);
      }
      setSelectedAircraftId(null);
    }
  }, [isOpen, hub, initialDestination]);

  // Calculate route info
  const routeInfo = useMemo(() => {
    if (!origin || !destination) return null;

    const orig = GLOBAL_AIRPORTS[origin];
    const dest = GLOBAL_AIRPORTS[destination];
    if (!orig || !dest) return null;

    // Calculate distance
    const R = 3440.065;
    const lat1 = orig.lat * Math.PI / 180;
    const lat2 = dest.lat * Math.PI / 180;
    const dLat = (dest.lat - orig.lat) * Math.PI / 180;
    const dLon = (dest.lon - orig.lon) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = Math.round(R * c);

    // Flight time (rough estimate)
    const speed = 450; // knots
    const flightTime = distance / speed;
    const hours = Math.floor(flightTime);
    const minutes = Math.round((flightTime - hours) * 60);

    return { distance, flightTime, hours, minutes, origin: orig, destination: dest };
  }, [origin, destination]);

  // Selected aircraft
  const selectedAircraft = aircraft.find(a => a.id === selectedAircraftId);

  // Range check
  const isRangeExceeded = useMemo(() => {
    if (!selectedAircraft || !routeInfo) return false;
    return routeInfo.distance > (selectedAircraft.range || 2000);
  }, [selectedAircraft, routeInfo]);

  // Runway check for both origin and destination
  const runwayCheck = useMemo(() => {
    if (!selectedAircraft) return { originOk: true, destOk: true };

    const originCheck = canAircraftLand(selectedAircraft.model, origin);
    const destCheck = canAircraftLand(selectedAircraft.model, destination);

    return {
      originOk: originCheck.canLand,
      originReason: originCheck.reason,
      destOk: destCheck.canLand,
      destReason: destCheck.reason,
    };
  }, [selectedAircraft, origin, destination]);

  const hasRunwayIssue = !runwayCheck.originOk || !runwayCheck.destOk;

  // Revenue estimate - MUST match RoutesPanel.jsx formula exactly!
  // Simple mode revenue: $10/nm per seat (5x AM4 economy)
  const revenueEstimate = useMemo(() => {
    if (!selectedAircraft || !routeInfo) return 0;
    const pax = Math.floor((selectedAircraft.capacity || 76) * 0.85); // 85% load factor
    const ticketPrice = 10 * routeInfo.distance; // $10 per nautical mile
    const revenue = Math.round(pax * ticketPrice);
    const costs = Math.round(routeInfo.distance * 1 + 500); // $1/nm + $500 base
    return { revenue, costs, profit: revenue - costs };
  }, [selectedAircraft, routeInfo]);

  // Handle create
  const handleCreate = useCallback(() => {
    if (!origin || !destination) return;
    if (origin === destination) return; // Prevent same-airport routes

    // Use unique ID to allow multiple routes to same destination
    const routeId = `${origin}-${destination}-${Date.now()}`;
    const route = {
      id: routeId,
      origin,           // Base origin (first airport in pair)
      destination,      // Base destination (second airport in pair)
      name: `${GLOBAL_AIRPORTS[origin]?.city || origin} ↔ ${GLOBAL_AIRPORTS[destination]?.city || destination}`,
      demand: 100,
      active: true,
      aircraftId: selectedAircraftId,
      autoDispatch: false,
      status: selectedAircraftId ? 'ready' : 'unassigned',
      lastFlightId: null,
      lastCompletedAt: null,
      totalFlights: 0,
      totalRevenue: 0,
      // Round-trip support: all routes are bidirectional
      isBidirectional: true,
      currentLegOrigin: origin,          // Where next flight departs FROM
      currentLegDestination: destination, // Where next flight goes TO
    };

    addRoute(route);
    onCreateRoute?.(route);
    onClose();
  }, [origin, destination, selectedAircraftId, addRoute, onCreateRoute, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div
        className="bg-zinc-900 rounded-lg max-w-lg w-full mx-4"
        style={{ border: "1px solid #333" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "#333" }}>
          <h2 className="text-lg font-bold" style={{ color: "#fff" }}>Create New Route</h2>
          <button onClick={onClose} style={{ color: "#737373", fontWeight: "bold", fontSize: "18px" }}>
            X
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Origin - HUB-ONLY: Can only depart from hubs you own */}
          <div>
            <label className="block text-sm mb-2" style={{ color: "#a1a1a1" }}>FROM (Your Hubs Only)</label>
            <AirportSearch
              value={origin}
              onChange={setOrigin}
              placeholder="Select departure hub..."
              excludeCode={destination}
              allowedAirports={allowedOrigins}
            />
          </div>

          {/* Destination - Can fly to any airport */}
          <div>
            <label className="block text-sm mb-2" style={{ color: "#a1a1a1" }}>TO</label>
            <AirportSearch
              value={destination}
              onChange={setDestination}
              placeholder={isSimulationMode ? "Select destination (slots required)" : "Search any destination..."}
              excludeCode={origin}
              allowedAirports={allowedDestinations}
              sortByDistanceFrom={origin}
            />
          </div>

          {/* Hub info hint */}
          {isStrandedOrigin ? (
            <div className="text-xs p-2 rounded" style={{ backgroundColor: "#1c1302", color: "#f59e0b" }}>
              ⚠ Return Flight: {origin} is not your hub. Aircraft here can only fly back to one of your hubs ({playerHubs.join(', ')}).
            </div>
          ) : (
            <div className="text-xs p-2 rounded" style={{ backgroundColor: "#0d1f1a", color: "#60ffd0" }}>
              Your hubs: {playerHubs.join(', ')}.
              {strandedLocations.length > 0 && (
                <span style={{ color: "#f59e0b" }}> Aircraft stranded at: {strandedLocations.join(', ')}</span>
              )}
            </div>
          )}

          {/* Simulation mode hint */}
          {isSimulationMode && (
            <div className="text-xs p-2 rounded" style={{ backgroundColor: "#1e3a5f", color: "#93c5fd" }}>
              Simulation Mode: Only airports where you have slots are available.
              Negotiate for slots at new airports to expand your network.
            </div>
          )}

          {/* Aircraft Selection */}
          <div>
            <label className="block text-sm mb-2" style={{ color: "#a1a1a1" }}>AIRCRAFT (Optional)</label>
            <select
              value={selectedAircraftId || ""}
              onChange={(e) => setSelectedAircraftId(e.target.value || null)}
              className="w-full p-3 rounded-lg bg-zinc-900 text-white"
              style={{ border: "1px solid #525252" }}
            >
              <option value="">Assign later...</option>
              {availableAircraft.map(ac => (
                <option key={ac.id} value={ac.id}>
                  {ac.registration} - {ac.model || ac.type} ({ac.capacity || 76} pax, {ac.range || 2000} nm)
                </option>
              ))}
            </select>
          </div>

          {/* Route Preview */}
          {routeInfo && (
            <div className="p-4 rounded-lg" style={{ backgroundColor: "#0c0c0c", border: "1px solid #1a1a1a" }}>
              <div className="text-sm font-bold mb-3" style={{ color: "#a1a1a1" }}>ROUTE PREVIEW</div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span style={{ color: "#737373" }}>Distance:</span>
                  <span className="ml-2" style={{ color: "#fff" }}>{routeInfo.distance} nm</span>
                </div>
                <div>
                  <span style={{ color: "#737373" }}>Flight time:</span>
                  <span className="ml-2" style={{ color: "#fff" }}>{routeInfo.hours}h {routeInfo.minutes}m</span>
                </div>
              </div>

              {selectedAircraft && (
                <div className="mt-3 pt-3 border-t" style={{ borderColor: "#333" }}>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span style={{ color: "#737373" }}>Est. revenue:</span>
                      <span className="ml-2" style={{ color: "#22c55e" }}>{formatMoney(revenueEstimate.revenue)}</span>
                    </div>
                    <div>
                      <span style={{ color: "#737373" }}>Est. profit:</span>
                      <span className="ml-2" style={{ color: "#22c55e" }}>{formatMoney(revenueEstimate.profit)}</span>
                    </div>
                  </div>
                  {/* Dynamic pricing info */}
                  <div className="mt-3 p-2 rounded flex items-start gap-2" style={{ backgroundColor: "#1a1a1a", border: "1px solid #333" }}>
                    <span style={{ color: "#60ffd0", fontSize: "16px" }}>💡</span>
                    <div className="text-xs" style={{ color: "#a3a3a3" }}>
                      Ticket prices adjust dynamically based on fuel costs, demand, and route freshness
                    </div>
                  </div>
                </div>
              )}

              {/* Range check */}
              {selectedAircraft && (
                <div className="mt-3 pt-3 border-t" style={{ borderColor: "#333" }}>
                  {isRangeExceeded ? (
                    <div className="flex items-start gap-2 p-2 rounded" style={{ backgroundColor: "#1f0a0a", border: "1px solid #ef4444" }}>
                      <span className="flex-shrink-0 font-bold" style={{ color: "#ef4444" }}>!</span>
                      <div>
                        <div className="font-bold" style={{ color: "#ef4444" }}>RANGE WARNING</div>
                        <div className="text-sm" style={{ color: "#fca5a5" }}>
                          Aircraft range: {selectedAircraft.range || 2000} nm<br />
                          Route distance: {routeInfo.distance} nm<br />
                          This aircraft CANNOT complete this route safely.
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2" style={{ color: "#22c55e" }}>
                      <span className="text-sm">
                        OK - Aircraft range: {selectedAircraft.range || 2000} nm - Sufficient
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Runway check */}
              {selectedAircraft && hasRunwayIssue && (
                <div className="mt-3 pt-3 border-t" style={{ borderColor: "#333" }}>
                  <div className="flex items-start gap-2 p-2 rounded" style={{ backgroundColor: "#1f1f0a", border: "1px solid #eab308" }}>
                    <span className="flex-shrink-0 font-bold" style={{ color: "#eab308" }}>⚠</span>
                    <div>
                      <div className="font-bold" style={{ color: "#eab308" }}>RUNWAY WARNING</div>
                      <div className="text-sm" style={{ color: "#fef08a" }}>
                        {!runwayCheck.originOk && (
                          <div>Origin: {runwayCheck.originReason}</div>
                        )}
                        {!runwayCheck.destOk && (
                          <div>Destination: {runwayCheck.destReason}</div>
                        )}
                        <div className="mt-1 text-xs" style={{ color: "#facc15" }}>
                          Aircraft may not be able to land safely. Route creation allowed but flight may divert.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t" style={{ borderColor: "#333" }}>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleCreate}
            disabled={!origin || !destination}
            style={{
              backgroundColor: origin && destination ? "#22c55e" : "#1a1a1a",
              color: origin && destination ? "#000" : "#525252",
            }}
          >
            Create Route
          </Button>
        </div>
      </div>
    </div>
  );
}
